import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import usePageMeta from '../hooks/usePageMeta';
import useHotjar from '../hooks/useHotjar';
import '../styles/about-risqly.css';

const PRINCIPLES = [
  {
    title: 'Clarity Over Noise',
    body: 'Security teams do not need more dashboards. They need meaningful signals that explain where to act first and why that action matters.'
  },
  {
    title: 'Shared Resilience',
    body: 'Vendor risk is connected risk. Risqly is built to help organizations understand exposure across dependencies and improve outcomes together.'
  },
  {
    title: 'Practical Transparency',
    body: 'Risk intelligence should be explainable, auditable, and useful in day-to-day decisions, not hidden behind black-box language.'
  }
];

const ROADMAP = [
  'Expand network-level dependency mapping for earlier concentration-risk detection.',
  'Strengthen verification workflows so organizations can establish public trust quickly.',
  'Deliver clearer executive reporting to connect technical findings with business impact.',
  'Grow community guidance and playbooks for remediation patterns that work in practice.'
];

function AboutRisqly() {
  usePageMeta({
    title: 'About',
    description: 'Learn why Risqly was built and how we improve trusted visibility across vendor ecosystems.',
  });
  useHotjar();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="about-risqly-root">
      <Header />
      <main className="about-risqly-main">
        <section className="about-risqly-hero">
          <p className="about-risqly-kicker">About Risqly Cybersecurity</p>
          <h1>Building trusted visibility across vendor ecosystems</h1>
          <p>
            Risqly exists to help organizations move from fragmented third-party assessments to a
            connected view of risk. We combine internal posture context, external dependency signals,
            and practical verification to support faster, more confident decisions.
          </p>
        </section>

        <section className="about-risqly-section">
          <h2>Why We Built Risqly</h2>
          <p>
            Security exposure rarely stays isolated to one vendor relationship. Shared infrastructure,
            subcontractors, and identity chains can amplify disruption across multiple organizations.
            Risqly was designed to make those relationships visible and actionable so teams can focus
            on the highest-impact controls first.
          </p>
        </section>

        <section className="about-risqly-section">
          <h2>How We Work</h2>
          <div className="about-risqly-principles-grid">
            {PRINCIPLES.map((item) => (
              <article className="about-risqly-principle" key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="about-risqly-section">
          <h2>What Comes Next</h2>
          <ul className="about-risqly-roadmap">
            {ROADMAP.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        <section className="about-risqly-cta">
          <h2>Follow Our Progress</h2>
          <p>
            We publish practical notes and implementation guidance on verification, vendor risk, and
            ecosystem resilience.
          </p>
          <div className="about-risqly-cta-actions">
            <Link to="/blog" className="btn btn-primary">Read the Blog</Link>
            <Link to="/contact" className="btn btn-secondary">Contact Risqly Cybersecurity</Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default AboutRisqly;
